<template>
	<div style="text-align: center" :key="backKey">
		<template v-if="scans_for_type === 'rfid'">
			<scanRFIDButton
				:user_name="user_name"
				:credential_title="credential_title"
				:initial_value="initial_value"
				@onValidate="(rfid) => onValidateCode(rfid)"
				@onScannedRfid="(rfid) => onItemScanned(rfid)"
				@back="backKey++"
				ref="scanDecalBtnRef"
			/>
		</template>

		<div v-if="scans_for_type === 'barcode'">
			<scan-qr-button
				:user_name="user_name"
				:credential_title="credential_title"
				:initial_value="initial_value"
				@onValidate="(qr) => onValidateCode(qr)"
				@onScannedQr="(qr) => onItemScanned(qr)"
				@back="backKey++"
				ref="scanDecalBtnRef"
			/>
		</div>

		<div v-if="scans_for_type === 'none'">
			<disburse-unscanned-button
				:user_name="user_name"
				:credential_title="credential_title"
				:initial_value="initial_value"
				@given="onItemScanned(null)"
				ref="scanDecalBtnRef"
			/>
		</div>

		<ErrorPopup
			:show="shouldShowError"
			:error-title="errorTitle"
			:error-message="errorMessage"
			:btn-text="errorBtnText"
			:close-btn-class="errorCloseBtnClass"
			:show-proceed-btn="showErrorProceedBtn"
			:proceed-btn-text="errorProceedBtnText"
			:proceed-btn-class="errorProceedBtnClass"
			@close="closeError"
			@proceed="useMismatchedBarcode"
		/>
	</div>
</template>

<script>
import ScanRFIDButton from "./ScanRFIDButton.vue";
import ScanQrButton from "@/components/ScanQrButton.vue";
import DisburseUnscannedButton from "@/components/DisburseUnscannedButton.vue";
import ErrorPopup from "@/components/ErrorPopup.vue";
import axios from "axios";

export default {
	name: 'ScanCredentials',
	components: {
		ScanQrButton,
		ScanRFIDButton,
		DisburseUnscannedButton,
		ErrorPopup
	},
	props: {
		party_id: {
			type: Number,
			required: true
		},
		user_name: {
			type: String,
			required: true
		},
		party_item_id: {
			type: Number,
			required: true
		},
		credential_title: {
			type: String,
			required: true
		},
		scans_for_type: {
			type: String,
			required: true
		},
		validated_prefix: {
			type: String,
			default: ""
		},
		initial_value: {
			type: String,
			default: ""
		},
	},
	data() {
		return {
			partyItemsToProcess: [],
			partyUsers: [],
			scannedValue: "",
			pendingMismatchedValue: null,
			shouldShowError: false,
			errorTitle: "Error",
			errorMessage: "test",
			errorBtnText: "",
			errorCloseBtnClass: null,
			showErrorProceedBtn: false,
			errorProceedBtnText: "Proceed",
			errorProceedBtnClass: "blue",
			backKey: 0,
		}
	},
	methods: {
		onValidateCode(value) {
			const scannerRegKey = localStorage.getItem('scanner_regkey');
			axios.defaults.headers.common['X-Auth-Token'] = scannerRegKey;
			axios.post(`${this.$scanner.api_server}/api/scannerdevice/validateCode`, {
				check: scannerRegKey,
				barcode_or_rfid: value,
			})
				.then(() => {
					if (this.$refs.scanDecalBtnRef && this.$refs.scanDecalBtnRef.proceedAfterValidation) {
						this.$refs.scanDecalBtnRef.proceedAfterValidation();
					}
				})
				.catch(err => {
					if (err.response !== undefined && err.response.status === 422) {
						this.$refs.scanDecalBtnRef.startScanning()
						this.errorTitle = "Error"
						this.errorMessage = "This code is invalid. Please return this credential to your supervisor."
						this.shouldShowError = true
					} else {
						//Don't bother handling other errors for this endpoint
					}
				})
		},
		onItemScanned(value) {
			if (this.shouldShowPrefixMismatch(value)) {
				this.showPrefixMismatch(value)
				return
			}

			this.disburseScannedValue(value)
		},
		disburseScannedValue(value) {
			this.scannedValue = value
			this.disbursePartyItem(value)
		},
		getValidatedPrefix() {
			const prefix = this.validated_prefix ? this.validated_prefix.toString() : ''
			return prefix
		},
		getErrorMessageValue(value) {
			if (value === null || value === undefined) {
				return ''
			}

			return value.toString()
		},
		getPrefixMismatchMessage(value) {
			const title = this.getErrorMessageValue(this.credential_title)
			const code = this.getErrorMessageValue(value)
			const prefix = this.getValidatedPrefix()
			return `Scanned ${title} code "${code}" doesn't start with expected prefix: "${prefix}". Try again?`
		},
		shouldShowPrefixMismatch(value) {
			const prefix = this.getValidatedPrefix()
			if (!prefix || value === null || value === undefined) {
				return false
			}

			return !value.toString().startsWith(prefix)
		},
		showPrefixMismatch(value) {
			this.pendingMismatchedValue = value
			this.errorTitle = "Error!"
			this.errorMessage = this.getPrefixMismatchMessage(value)
			this.errorBtnText = "ok"
			this.errorCloseBtnClass = "purple"
			this.showErrorProceedBtn = true
			this.errorProceedBtnText = "Use mismatched barcode"
			this.errorProceedBtnClass = "grey"
			this.shouldShowError = true
		},
		useMismatchedBarcode() {
			const value = this.pendingMismatchedValue
			this.closeError()
			this.disburseScannedValue(value)
		},
		resetErrorState() {
			this.errorTitle = "Error"
			this.errorMessage = ""
			this.errorBtnText = ""
			this.errorCloseBtnClass = null
			this.showErrorProceedBtn = false
			this.errorProceedBtnText = "Proceed"
			this.errorProceedBtnClass = "blue"
			this.pendingMismatchedValue = null
		},
		disbursePartyItem(value) {
			window.scanner.loader.open()

			const scannerRegKey = localStorage.getItem('scanner_regkey');
			axios.defaults.headers.common['X-Auth-Token'] = scannerRegKey;
			axios.post(`${this.$scanner.api_server}/api/scannerdevice/party/${this.party_id}/disbursePartyItem`, {
				check: scannerRegKey,
				party_item_id: this.party_item_id,
				barcode_or_rfid: value,
			})
				.then(() => {
					this.$emit('onCredentialScanned', this.scannedValue);
					this.$nextTick(() => { this.backKey++ })
				})
				.catch(err => {
					if (err.response !== undefined && err.response.status === 422) {
						if (this.$refs.scanDecalBtnRef) {
							this.$refs.scanDecalBtnRef.startScanning()
						}
						this.errorTitle = "Error"
						this.errorMessage = Object.values(err.response.data.errors).join("\n\n")
						this.shouldShowError = true
					} else if (err.response !== undefined && err.response.status === 417) {
						this.shouldShowError = true
						this.errorTitle = "Error"
						this.errorMessage = err.response.data.message
					} else {
						// non 422 is uh oh time
						this.shouldShowError = true
						this.errorTitle = "Error"
						this.errorMessage = 'Big time Error, contact champion support'
					}
					this.backKey++;
				})
				.finally(() => {
					window.scanner.loader.close()
				})
		},
		closeError() {
			this.shouldShowError = false
			this.resetErrorState()
		}
	}
}
</script>

<style scoped>
.flex {
	display: flex;
	justify-content: space-between;
}

.grey-background {
	background: #666 !important;
}
</style>
